import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useNotificationPreferences } from '@/hooks/useNotifications';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { approvalService } from '@/services/approval.service';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { EMPTY_BOARD_FILTERS } from '@/types/releaseBoard';
import { useReleaseMomentStore, type ReleaseMomentKind } from '@/stores/releaseMoment.store';
import { toast } from '@/utils/toast';
import './releaseMoment.css';

interface MomentFacts {
  token: number;
  allCaughtUp: boolean;
}

export const ReleaseMomentCard = ({
  kind,
  calm,
  mainLine,
  statusLine,
}: {
  kind: ReleaseMomentKind;
  calm: boolean;
  mainLine: string;
  statusLine: string | null;
}) => (
  <div data-testid="release-moment" className={`release-moment relative flex w-[min(100%,360px)] items-center gap-3 overflow-hidden rounded-2xl border px-4 py-3 text-gray-900 shadow-xl ${calm ? 'border-gray-200 bg-white' : 'border-primary-300 bg-primary-25'}`}>
    <div className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${calm ? 'bg-gray-100 text-gray-700' : 'bg-primary-50 text-primary-600'}`}>
      <Icon name={kind === 'released' ? ICONS.RELEASE_STAMP : ICONS.CHECK_CIRCLE} size="md" ariaHidden />
      {!calm && <span className="release-moment__dots" aria-hidden="true"><i /><i /><i /><i /></span>}
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold">{mainLine}</p>
      {statusLine && <p className="mt-0.5 text-xs text-gray-600">{statusLine}</p>}
    </div>
  </div>
);

/** Small on-brand action feedback; the release undo offer remains fully independent. */
export const ReleaseMoment = () => {
  const { t } = useTranslation('common');
  const { teamId } = useEffectiveTeamId();
  const { isCreator } = usePostPermissions();
  const { data: preferences, isError: preferenceError } = useNotificationPreferences();
  const entry = useReleaseMomentStore((state) => state.entry);
  const dismiss = useReleaseMomentStore((state) => state.dismiss);
  const [facts, setFacts] = useState<MomentFacts | null>(null);
  const token = entry?.token;
  const kind = entry?.kind;
  const quantity = entry?.quantity;
  const plainToast = entry?.plainToast;
  const celebrationsEnabled = preferences?.celebrationsEnabled;

  useEffect(() => {
    if (token === undefined || !kind || quantity === undefined) return;
    const plainFeedback = () => {
      if (plainToast) toast.success(quantity > 1
        ? kind === 'sent'
          ? t('release_bulk_sent', { count: quantity })
          : t('release_undo_bulk_released', { count: quantity })
        : kind === 'sent' ? t('release_moment_sent_calm') : t('release_moment_released_calm'));
      dismiss();
    };
    if (celebrationsEnabled === false || preferenceError) {
      plainFeedback();
      return;
    }
    // Preference reads may be slow or never settle. An action always gets feedback.
    if (celebrationsEnabled === undefined) {
      const fallback = window.setTimeout(plainFeedback, 1200);
      return () => window.clearTimeout(fallback);
    }
    let active = true;
    setFacts(null);
    if (teamId !== null && kind === 'released') {
      void approvalService.getReleaseBoardCounts(teamId, EMPTY_BOARD_FILTERS).then((remaining) => {
        if (active) setFacts({ token, allCaughtUp: remaining.in_review === 0 });
      }).catch(() => {
        // The action succeeded; omit queue state if the follow-up read fails.
      });
    }
    const timer = window.setTimeout(dismiss, 4200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [token, kind, quantity, plainToast, celebrationsEnabled, preferenceError, teamId, dismiss, t]);

  if (!entry || celebrationsEnabled !== true) return null;

  const calm = entry.kind === 'released' || !isCreator;
  const mainLine = entry.kind === 'sent' ? t('release_moment_sent_calm') : t('release_moment_released_calm');
  const statusLine = entry.kind === 'released' && facts?.token === entry.token && facts.allCaughtUp
    ? t('release_moment_all_caught_up')
    : null;

  return (
    <div className="pointer-events-none fixed inset-x-3 top-20 z-toast flex justify-center" role="status" aria-live="polite">
      <ReleaseMomentCard key={entry.token} kind={entry.kind} calm={calm} mainLine={mainLine} statusLine={statusLine} />
    </div>
  );
};
